/**
 * Admin App — Environment-based configuration.
 * Centralizes all external URLs and settings.
 *
 * Stacks supported (set REACT_APP_STACK):
 *   - local : same-origin / localhost (CRA dev proxy → API gateway at :80)
 *   - pie   : PIE (pre-integration / dev cloud) stack
 *   - stg   : Staging stack
 *   - prod  : Production stack
 *
 * URL resolution order (highest priority first):
 *   1. Explicit REACT_APP_<SVC>_URL env var (if non-empty)
 *   2. Stack default from STACK_URLS table below
 *   3. Empty string (same-origin via /api/v1/... through edge proxy)
 *
 * NOTE: The PIE / STG / PROD hostnames below are placeholders — replace with the
 * actual gateway URLs once provisioned.
 */

type Stack = 'local' | 'pie' | 'stg' | 'prod';

type ServiceKey =
  | 'apiBase'
  | 'ums' | 'lms' | 'pms' | 'ams' | 'nms'
  | 'sms' | 'rms' | 'pyms' | 'hms' | 'cms' | 'ocr';

const STACK: Stack = (
  (process.env.REACT_APP_STACK || 'local').toLowerCase() as Stack
);

const STACK_URLS: Record<Stack, Record<ServiceKey, string>> = {
  // Local dev — leave blank so CRA's dev proxy forwards /api/* to the gateway at :80
  local: {
    apiBase: '',
    ums: '', lms: '', pms: '', ams: '', nms: '',
    sms: '', rms: '', pyms: '', hms: '', cms: '', ocr: '',
  },
  // PIE (pre-integration / dev cloud) — single gateway URL, sample only
  pie: {
    apiBase: 'https://api.pie.wary.in',
    ums:  'https://api.pie.wary.in',
    lms:  'https://api.pie.wary.in',
    pms:  'https://api.pie.wary.in',
    ams:  'https://api.pie.wary.in',
    nms:  'https://api.pie.wary.in',
    sms:  'https://api.pie.wary.in',
    rms:  'https://api.pie.wary.in',
    pyms: 'https://api.pie.wary.in',
    hms:  'https://api.pie.wary.in',
    cms:  'https://api.pie.wary.in',
    ocr:  'https://api.pie.wary.in',
  },
  // Staging — sample only
  stg: {
    apiBase: 'https://api.stg.wary.in',
    ums:  'https://api.stg.wary.in',
    lms:  'https://api.stg.wary.in',
    pms:  'https://api.stg.wary.in',
    ams:  'https://api.stg.wary.in',
    nms:  'https://api.stg.wary.in',
    sms:  'https://api.stg.wary.in',
    rms:  'https://api.stg.wary.in',
    pyms: 'https://api.stg.wary.in',
    hms:  'https://api.stg.wary.in',
    cms:  'https://api.stg.wary.in',
    ocr:  'https://api.stg.wary.in',
  },
  // Production — sample only
  prod: {
    apiBase: 'https://api.wary.in',
    ums:  'https://api.wary.in',
    lms:  'https://api.wary.in',
    pms:  'https://api.wary.in',
    ams:  'https://api.wary.in',
    nms:  'https://api.wary.in',
    sms:  'https://api.wary.in',
    rms:  'https://api.wary.in',
    pyms: 'https://api.wary.in',
    hms:  'https://api.wary.in',
    cms:  'https://api.wary.in',
    ocr:  'https://api.wary.in',
  },
};

const stackDefaults = STACK_URLS[STACK] || STACK_URLS.local;

const pick = (envValue: string | undefined, fallback: string): string =>
  envValue && envValue.trim().length > 0 ? envValue : fallback;

const config = {
  stack: STACK,

  // API Gateway base URL — empty on `local` so the app works behind any edge proxy
  apiBaseUrl: pick(process.env.REACT_APP_API_BASE_URL, stackDefaults.apiBase),

  // Direct service URLs (fallback if not using API gateway)
  services: {
    ums:  pick(process.env.REACT_APP_UMS_URL,  stackDefaults.ums),
    lms:  pick(process.env.REACT_APP_LMS_URL,  stackDefaults.lms),
    pms:  pick(process.env.REACT_APP_PMS_URL,  stackDefaults.pms),
    ams:  pick(process.env.REACT_APP_AMS_URL,  stackDefaults.ams),
    nms:  pick(process.env.REACT_APP_NMS_URL,  stackDefaults.nms),
    sms:  pick(process.env.REACT_APP_SMS_URL,  stackDefaults.sms),
    rms:  pick(process.env.REACT_APP_RMS_URL,  stackDefaults.rms),
    pyms: pick(process.env.REACT_APP_PYMS_URL, stackDefaults.pyms),
    hms:  pick(process.env.REACT_APP_HMS_URL,  stackDefaults.hms),
    cms:  pick(process.env.REACT_APP_CMS_URL,  stackDefaults.cms),
    ocr:  pick(process.env.REACT_APP_OCR_URL,  stackDefaults.ocr),
  },

  // App settings
  appName: 'WARY Admin',
  tokenCookieName: 'token',
  defaultPageSize: 10,
};

export default config;
